import { Box, Link, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import Compensation from 'modules/contracts/store/models/Compensation'
import {
  EXPENSABLE_CLASS,
  EXPENSE_CLASS,
  MILEAGE_CLASS,
} from 'modules/expenses/lib/expensables/expensable'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import { useExpensables } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import {
  ATTENDANCE_COMPENSATION_SOURCE_TYPE,
  TIMEBLOCK_SOURCE_TYPE,
} from 'modules/payroll_bundle/lib/constants'
import Supplement from 'modules/payroll_bundle/store/models/Supplement'
import { CustomTimeRangeCategory } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

import { convertMinutesToHoursString } from './tooltipContentHelper'

interface Props {
  form: SupplementForm
  category?: CustomTimeRangeCategory | null | undefined
}

interface TooltipInfoProps {
  title: string
  information?: string
}

const TooltipInfo = ({ title, information }: TooltipInfoProps) => {
  return (
    <Box gap="s4">
      <Text.Normal size="200" weight="semibold" caps>
        {title}
      </Text.Normal>
      <Text.Normal size="200">{information}</Text.Normal>
    </Box>
  )
}

const capitalizeFirstLetter = (s: string) => {
  const strippedString = s.split('_').join(' ')
  return strippedString.charAt(0).toUpperCase() + strippedString.slice(1)
}

const perWorkedDayInfo = (form: SupplementForm) => {
  const supplementUnit = form.get('unit').value
  const description = form.get('description').value.split(',')
  const daysWorked: string = description[0]
  const daysWorkedCount = parseInt(description[1])

  const amountPerDay: string =
    supplementUnit === Supplement.units.money
      ? i18n.t('payroll_bundle.supplements.tool_tip_content.money_amount_per_day', {
          count: daysWorkedCount,
          currency: form.employee && employeeLegalEntity(form.employee).currency,
        })
      : i18n.tp('payroll_bundle.supplements.tool_tip_content.units_amount_per_day', {
          count: daysWorkedCount,
        })
  return { daysWorked, amountPerDay }
}

const getCategoryTimeType = (category: CustomTimeRangeCategory) => {
  if (category.isOvertime) {
    return i18n.t('time_categories.overtime')
  }

  if (category.isRegular) {
    return i18n.t('time_categories.regular')
  }
}

const compensationTypeLabel = (compensationType: string): string => {
  const labels = Compensation.Types()
  return labels[compensationType] || compensationType
}

const TooltipContent = ({ form, category }: Props) => {
  const EXPENSABLE_SOURCE_TYPE = `::${EXPENSABLE_CLASS}`
  const EXPENSES_SOURCE_TYPE = `::${EXPENSE_CLASS}`
  const MILEAGE_SOURCE_TYPE = `::${MILEAGE_CLASS}`
  const { navigation } = useNavigation()

  const { expensablesById } = useExpensables()

  const sourceType = form.get('source_type').value
  const sourceId = form.get('source_id').value
  const createdAt = form.get('created_at').value
  const bulkImportId = form.get('supplement_bulk_import_id').value
  const createdBy = parseInt(form.get('created_by_access_id').value, 10)
  const supplementCreationDate = stringToDate(dateBackendDateTimeToUI(createdAt), 'ui').format('ll')

  const supplementAuthor = users.get(createdBy)
  const supplementAuthorName = supplementAuthor ? `${supplementAuthor.fullName()}` : '-'

  const sourceDescription = useMemo(() => {
    switch (sourceType) {
      case ATTENDANCE_COMPENSATION_SOURCE_TYPE:
        return (
          <Box textAlign="left" gap="s16" gapDivider>
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.type')}
              information={i18n.t('payroll_bundle.supplements.tool_tip_content.overtime')}
            />
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.sent_to_payroll')}
              information={`${supplementCreationDate},  ${supplementAuthorName}`}
            />
          </Box>
        )
      case EXPENSABLE_SOURCE_TYPE:
      case MILEAGE_SOURCE_TYPE:
      case EXPENSES_SOURCE_TYPE:
        const expensable =
          sourceType === EXPENSABLE_SOURCE_TYPE ? expensablesById[Number(sourceId)] : null
        const expenseCategory = expensable?.expense?.category?.name
        const id = expensable?.id || `${sourceType.split('::')[2]}-${sourceId}`
        const groupId = expensable?.groupId
        const path = groupId
          ? navigation.expenses.groups.id.expenseId.fullPath({ groupId: groupId, expense_id: id })
          : navigation.expenses.list.expenses.expenseId.fullPath({ expense_id: id })

        return (
          <Box textAlign="left" gap="s16" gapDivider>
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.expense_text')}
              information={expenseCategory}
            />
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.sent_to_payroll')}
              information={`${supplementCreationDate},  ${supplementAuthorName}`}
            />
            <Link.Router to={path}>
              <Text.Normal color="teal1000" size="200">
                {i18n.t('payroll_bundle.supplements.tool_tip_content.go_to_expense')}
              </Text.Normal>
            </Link.Router>
          </Box>
        )
      case TIMEBLOCK_SOURCE_TYPE:
        const raw_hours = convertMinutesToHoursString(form.get('raw_minutes_in_cents').value)
        const equivalent_hours = convertMinutesToHoursString(
          form.get('equivalent_minutes_in_cents').value
        )

        return (
          <Box textAlign="left" gap="s16" gapDivider>
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.type')}
              information={
                category
                  ? getCategoryTimeType(category)
                  : i18n.t('payroll_bundle.supplements.tool_tip_content.overtime')
              }
            />
            {!!category && (
              <TooltipInfo
                title={i18n.t('payroll_bundle.supplements.tool_tip_content.time_category')}
                information={category?.name}
              />
            )}
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.time_equivalence')}
              information={`${raw_hours} = ${equivalent_hours}`}
            />
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.sent_to_payroll')}
              information={`${supplementCreationDate},  ${supplementAuthorName}`}
            />
          </Box>
        )

      default:
        return null
    }
  }, [
    sourceId,
    expensablesById,
    navigation.expenses,
    sourceType,
    supplementAuthorName,
    supplementCreationDate,
    form,
    category,
  ])

  const creationDateTitle = bulkImportId
    ? i18n.t('payroll_bundle.supplements.tool_tip_content.import_date')
    : i18n.t('payroll_bundle.supplements.tool_tip_content.creation_date')

  if (!!form.get('contracts_compensation_id').value) {
    return (
      <Box textAlign="left" gap="s16" gapDivider>
        <TooltipInfo
          title={i18n.t('payroll_bundle.supplements.tool_tip_content.amount_type')}
          information={capitalizeFirstLetter(compensationTypeLabel(form.compensationType))}
        />
        <TooltipInfo
          title={i18n.t('payroll_bundle.supplements.tool_tip_content.period_text')}
          information={i18n.tp(
            'payroll_bundle.supplements.tool_tip_content.supplement_recurrence',
            { count: form.compensationRecurrenceCount }
          )}
        />
        {form.compensationType == Compensation.types.per_worked_day && (
          <Box gap="s16" gapDivider>
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.days_worked_title')}
              information={perWorkedDayInfo(form).daysWorked}
            />
            <TooltipInfo
              title={i18n.t('payroll_bundle.supplements.tool_tip_content.amount_per_day_title')}
              information={perWorkedDayInfo(form).amountPerDay}
            />
          </Box>
        )}
      </Box>
    )
  }
  if (!!sourceType) {
    return sourceDescription
  }

  return (
    <Box textAlign="left">
      <TooltipInfo
        title={creationDateTitle}
        information={`${supplementCreationDate}, ${supplementAuthorName}`}
      />
    </Box>
  )
}

export default observer(TooltipContent)
